/**
 * SSOT overlay pravidla pro object-popup session draft.
 *
 * Web + dokumentace: tento modul v `@cubby/shared`.
 * Mobile: `mobile-shared/.../ObjectPopupSessionDraftPipeline.kt` musí zůstat v sync (stejná pravidla).
 */
import {
  isRozvadecZafukKind,
  type RozvadecZafukEntry,
} from '../constants/object-workflow/rozvadec-zafuk.constants';
import {
  buildMaterialsBaselineJsonFromDraftRows,
  hasMeaningfulExcavationData,
  serializeWorkflowObjectNotesForBaseline,
} from './object-popup-draft-baseline.helpers';
import type {
  ObjectPopupLocalDraft,
  ObjectPopupMaterialDraftRow,
  ObjectPopupSpecDraftSnapshot,
  WorkflowObjectNoteDraft,
} from './object-popup-local-draft.types';

export function sessionReadyForDraftPersist(materialUsageHydrated: boolean): boolean {
  return materialUsageHydrated;
}

export function resolveOverlayIfMeaningful<T>(
  draft: T | null | undefined,
  isMeaningful: (value: T) => boolean,
): T | null {
  if (draft == null) return null;
  if (!isMeaningful(draft)) return null;
  return draft;
}

export function resolvePendingNoteOverlay(draft: string | null | undefined): string | null {
  const trimmed = draft?.trim();
  return resolveOverlayIfMeaningful(trimmed || null, (v) => v.length > 0);
}

export function resolvePendingWeldCountOverlay(draft: number | null | undefined): number | null {
  return resolveOverlayIfMeaningful(draft ?? null, (v) => v >= 0);
}

export function resolveExcavationDataOverlay(
  data: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  return resolveOverlayIfMeaningful(data ?? null, (v) => hasMeaningfulExcavationData(v));
}

export function specSnapshotHasAnyContent(snapshot: ObjectPopupSpecDraftSnapshot): boolean {
  return (
    snapshot.hpCountTotal > 0 ||
    snapshot.hpNebytCount > 0 ||
    snapshot.horizontalniPropoj != null ||
    snapshot.babovkaPlacement.trim().length > 0 ||
    snapshot.ukonceniVRd.trim().length > 0 ||
    snapshot.ukonceniVRdOther.trim().length > 0 ||
    snapshot.zafukOkMetrazStartM.trim().length > 0 ||
    snapshot.zafukOkMetrazKonecM.trim().length > 0 ||
    snapshot.zafukVychoziBod ||
    snapshot.zafukTypOk.trim().length > 0 ||
    snapshot.zafukRozvadecObjectId.trim().length > 0 ||
    snapshot.zafukRozvadecName.trim().length > 0 ||
    snapshot.napojeneMtBarvy.length > 0 ||
    snapshot.floorsJson.trim().length > 2
  );
}

/**
 * Draft `hpCountTotal === 0` means unset (empty placeholder), not an intentional wipe of
 * hydrated server HP. Parita KMP `resolveHpCountTotalForMerge`.
 */
export function resolveHpCountTotalForDraftOverlay(
  draftHp: number,
  hydratedHp: number | null | undefined,
): number {
  if (draftHp > 0) return draftHp;
  if (hydratedHp != null && hydratedHp > 0) return hydratedHp;
  return draftHp;
}

export function resolveSpecOverlay(
  draft: ObjectPopupSpecDraftSnapshot | null | undefined,
): ObjectPopupSpecDraftSnapshot | null {
  return resolveOverlayIfMeaningful(draft ?? null, specSnapshotHasAnyContent);
}

export function resolveVrTechnicalReportOverlay(
  report: string | null | undefined,
  baseline: string | null | undefined,
): string | null {
  const trimmed = report?.trim() ?? '';
  if (!trimmed) return null;
  const base = baseline?.trim() ?? '';
  return resolveOverlayIfMeaningful(trimmed, (v) => v !== base);
}

export interface WorkflowNotesOverlay {
  notes: WorkflowObjectNoteDraft[];
  dirty: boolean;
  sessionBaselineJson: string | null;
}

export function resolveWorkflowNotesOverlay(draft: ObjectPopupLocalDraft): WorkflowNotesOverlay | null {
  if (draft.workflowNotesDirty) {
    return {
      notes: draft.workflowNotes,
      dirty: true,
      sessionBaselineJson: draft.workflowNotesSessionBaselineJson ?? null,
    };
  }
  const baseline = draft.workflowNotesSessionBaselineJson;
  // Only overlay when the draft list diverged from its session baseline.
  // A non-empty draft that still equals baseline must NOT replace a fresh
  // server hydrate (that previously hid other-phase / other-author notes).
  if (
    baseline != null &&
    serializeWorkflowObjectNotesForBaseline(draft.workflowNotes) !== baseline
  ) {
    return {
      notes: draft.workflowNotes,
      dirty: false,
      sessionBaselineJson: baseline,
    };
  }
  return null;
}

export interface MaterialsOverlay {
  selectedMaterials: ObjectPopupMaterialDraftRow[];
  rowBaseline: ObjectPopupMaterialDraftRow[];
  sessionBaselineJson: string | null;
  materialUsageHydrated: boolean;
}

export function resolveMaterialsOverlay(draft: ObjectPopupLocalDraft): MaterialsOverlay | null {
  if (!draft.materialUsageHydrated || draft.selectedMaterials.length === 0) return null;
  const baselineJson = draft.materialsSessionBaselineJson;
  const meaningful =
    baselineJson == null
      ? true
      : buildMaterialsBaselineJsonFromDraftRows(draft.selectedMaterials) !== baselineJson;
  if (!meaningful) return null;
  return {
    selectedMaterials: draft.selectedMaterials,
    rowBaseline: draft.selectedMaterialsRowBaseline,
    sessionBaselineJson: baselineJson ?? null,
    materialUsageHydrated: true,
  };
}

export interface RouteOverlay {
  leadObjectId: string | null;
  segmentIds: string[];
  pathAdvanceBatchId: string | null;
  targetNextPhase: string | null;
}

export function resolveRouteOverlay(draft: ObjectPopupLocalDraft): RouteOverlay | null {
  if (draft.confirmedRouteSegmentIds.length === 0) return null;
  return {
    leadObjectId: draft.confirmedRouteLeadObjectId ?? null,
    segmentIds: draft.confirmedRouteSegmentIds,
    pathAdvanceBatchId: draft.pathAdvanceBatchId ?? null,
    targetNextPhase: draft.confirmedRouteTargetNextPhase ?? null,
  };
}

export interface RozvadecMilestoneOverlay {
  milestoneId: string | null;
  specActive: boolean;
  weldCount: number | null;
}

export function resolveRozvadecMilestoneOverlay(
  draft: ObjectPopupLocalDraft,
): RozvadecMilestoneOverlay | null {
  const hasMilestone = !!draft.selectedRozvadecMilestoneId?.trim();
  const hasWeld = draft.rozvadecMilestoneWeldCount != null;
  const specActive = !!draft.rozvadecMilestoneSpecActive && hasMilestone;
  if (!hasMilestone && !hasWeld && !specActive) return null;
  return {
    milestoneId: draft.selectedRozvadecMilestoneId ?? null,
    specActive,
    weldCount: draft.rozvadecMilestoneWeldCount ?? null,
  };
}

export interface BudovaMilestoneOverlay {
  milestoneId: string | null;
  specActive: boolean;
}

/**
 * Restore BUDOVA milestone info only when the user was actively editing that milestone
 * (`budovaMilestoneSpecActive`). A bare `selectedBudovaMilestoneId` left from a prior
 * info view must NOT force popup 3 on reopen — that skipped popup 2 (picker) for
 * buildings with partial milestone progress.
 */
export function resolveBudovaMilestoneOverlay(
  draft: ObjectPopupLocalDraft,
): BudovaMilestoneOverlay | null {
  const hasMilestone = !!draft.selectedBudovaMilestoneId?.trim();
  const specActive = !!draft.budovaMilestoneSpecActive && hasMilestone;
  if (!specActive) return null;
  return {
    milestoneId: draft.selectedBudovaMilestoneId ?? null,
    specActive: true,
  };
}

export function hasMeaningfulRozvadecZafukDraftEntries(entries: RozvadecZafukEntry[]): boolean {
  if (entries.length === 0) return false;
  if (entries.length > 1) return true;
  const entry = entries[0];
  return (
    !!entry.vychoziBod ||
    isRozvadecZafukKind(entry.kind) ||
    !!(entry.sourceRozvadecObjectId?.trim()) ||
    entry.okMetrazStartM != null ||
    entry.okMetrazKonecM != null ||
    !!(entry.typOk?.trim())
  );
}

export interface RozvadecZafukOverlayResult {
  entries: RozvadecZafukEntry[];
  activeEntryId: string | null;
  vychoziBod: boolean;
}

export function resolveRozvadecZafukOverlay(
  draftRows: RozvadecZafukEntry[],
  draftActiveEntryId: string | null | undefined,
): RozvadecZafukOverlayResult | null {
  if (!hasMeaningfulRozvadecZafukDraftEntries(draftRows)) return null;
  const vychoziBod = draftRows.some((e) => e.vychoziBod);
  if (vychoziBod) {
    return { entries: [], activeEntryId: null, vychoziBod: true };
  }
  const normalized = draftRows.map((e) => ({ ...e, vychoziBod: false }));
  const activeId =
    draftActiveEntryId && normalized.some((e) => e.id === draftActiveEntryId)
      ? draftActiveEntryId
      : normalized[0]?.id ?? null;
  return { entries: normalized, activeEntryId: activeId, vychoziBod: false };
}

export function hasPersistableObjectPopupDraft(draft: ObjectPopupLocalDraft): boolean {
  if (draft.pendingNote?.trim()) return true;
  if (draft.pendingWeldCount != null) return true;
  if (draft.excavationData && hasMeaningfulExcavationData(draft.excavationData)) return true;
  if (draft.specDraft && specSnapshotHasAnyContent(draft.specDraft)) return true;
  const vr = draft.vrTechnicalReport?.trim() ?? '';
  const vrBase = draft.vrTechnicalReportBaseline?.trim() ?? '';
  if (vr && vr !== vrBase) return true;
  if (draft.confirmedRouteSegmentIds.length > 0) return true;
  if (draft.workflowNotesDirty) return true;
  if (
    draft.workflowNotes.length > 0 &&
    draft.workflowNotesSessionBaselineJson != null &&
    serializeWorkflowObjectNotesForBaseline(draft.workflowNotes) !==
      draft.workflowNotesSessionBaselineJson
  ) {
    return true;
  }
  if (draft.materialUsageHydrated && draft.selectedMaterials.length > 0) {
    if (
      draft.materialsSessionBaselineJson == null ||
      buildMaterialsBaselineJsonFromDraftRows(draft.selectedMaterials) !== draft.materialsSessionBaselineJson
    ) {
      return true;
    }
  }
  if (draft.selectedRozvadecMilestoneId && draft.rozvadecMilestoneWeldCount != null) return true;
  if (draft.rozvadecMilestoneSpecActive && draft.selectedRozvadecMilestoneId) return true;
  if (hasMeaningfulRozvadecZafukDraftEntries(draft.rozvadecZafukEntries)) return true;
  // Only an in-progress milestone spec edit — not a leftover selected id from popup 3.
  if (draft.budovaMilestoneSpecActive && draft.selectedBudovaMilestoneId?.trim()) return true;
  return false;
}
